import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  MdAssignment,
  MdSearch,
  MdEdit,
  MdSend,
  MdBarChart,
  MdSchedule,
  MdSecurity,
  MdVerifiedUser,
  MdBuild,
  MdTrendingUp,
  MdDomain,
  MdPerson,
  MdCheckCircle,
  MdSupervisorAccount,
  MdRefresh,
} from "react-icons/md";
import { useEvaluations } from "context/EvaluationsContext";
import { useSectorKpis } from "context/SectorKpiContext";
import { ROLES } from "context/WorkersContext";
import { getSectors } from "api/sectors";
import { getKpiUsersBySectorAndRole } from "api/workers";
import { createEvaluationScore } from "api/evaluationScores";
import { getSectorKpis } from "api/sectorKpis";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

// KPI icon per index (Safety last = shield)
const KPI_ICONS = [MdBarChart, MdVerifiedUser, MdBuild, MdTrendingUp, MdSecurity];

// Mock: current logged-in supervisor id (in real app from auth)
const CURRENT_SUPERVISOR_ID = "w-1";

// Shift label e.g. "4:29 pm Shift - Thu 29 Jan"
const getShiftLabel = () => {
  const d = new Date();
  const time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/^0/, "").trim();
  const dateStr = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(/,/g, "");
  return `${time} Shift - ${dateStr}`;
};

export default function Evaluation() {
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [selectedSectorId, setSelectedSectorId] = useState("");
  const [evaluateeRole, setEvaluateeRole] = useState(ROLES.OPERATOR);
  const [selectedEvaluateeId, setSelectedEvaluateeId] = useState("");
  const [scores, setScores] = useState({});
  const [percentageScores, setPercentageScores] = useState({});
  const [kpiNotes, setKpiNotes] = useState({});
  const [actuals, setActuals] = useState({});
  const [, setDraft] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState("");
  const [sectorSearch, setSectorSearch] = useState("");
  const [personnelInSector, setPersonnelInSector] = useState([]);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sectorKpis, setSectorKpis] = useState([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisError, setKpisError] = useState(null);
  const [evaluatedAtLocal, setEvaluatedAtLocal] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const { evaluations, addEvaluation } = useEvaluations();

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    try {
      const result = await getSectors();
      const list = (result.data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        icon_key: s.icon_key,
      }));
      setSectors(list);
    } catch {
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  useEffect(() => {
    if (sectors.length > 0 && !selectedSectorId) {
      setSelectedSectorId(sectors[0].id);
    }
  }, [sectors, selectedSectorId]);

  const filteredSectors = useMemo(() => {
    if (!sectorSearch.trim()) return sectors;
    const q = sectorSearch.trim().toLowerCase();
    return sectors.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [sectors, sectorSearch]);

  const mapKpiRow = (row) => ({
    id: row.id,
    name: row.name,
    definition: row.definition ?? row.description ?? "",
    description: row.definition ?? row.description ?? "",
    target: row.target ?? row.target_value ?? "",
  });

  const fetchKpis = useCallback(async (sectorId, role) => {
    if (!sectorId) {
      setSectorKpis([]);
      return;
    }
    setKpisLoading(true);
    setKpisError(null);
    try {
      const result = await getSectorKpis(sectorId, role);
      setSectorKpis((result.data || []).map(mapKpiRow));
    } catch (err) {
      setKpisError(err.response?.data?.message || err.message || "Failed to load KPIs");
      setSectorKpis([]);
    } finally {
      setKpisLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKpis(selectedSectorId, evaluateeRole);
  }, [selectedSectorId, evaluateeRole, fetchKpis]);

  const selectedSector = sectors.find((s) => s.id === selectedSectorId) || null;

  useEffect(() => {
    if (!selectedSectorId || !selectedSector?.name) {
      setPersonnelInSector([]);
      return;
    }
    const roleParam = evaluateeRole === ROLES.OPERATOR ? "operator" : "supervisor";
    setPersonnelLoading(true);
    getKpiUsersBySectorAndRole(selectedSector.name, roleParam)
      .then((result) => {
        const list = (result.data || []).map((u) => ({
          id: u.id,
          name: u.name || "",
          email: u.email || "",
          employeeId: u.employee_id || "",
          role: u.role,
          sector_name: u.sector_name,
        }));
        setPersonnelInSector(list);
      })
      .catch(() => setPersonnelInSector([]))
      .finally(() => setPersonnelLoading(false));
  }, [selectedSectorId, selectedSector?.name, evaluateeRole]);

  const filteredPersonnel = useMemo(() => {
    if (!personnelSearch.trim()) return personnelInSector;
    const q = personnelSearch.trim().toLowerCase();
    return personnelInSector.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [personnelInSector, personnelSearch]);

  const selectedEvaluatee = personnelInSector.find((p) => p.id === selectedEvaluateeId);

  const lastEntryForEvaluatee = useMemo(() => {
    if (!selectedEvaluateeId) return null;
    const ev = evaluations.find(
      (e) => (e.evaluateeId || e.operatorId) === selectedEvaluateeId && e.sectorId === selectedSectorId && (e.evaluateeRole || ROLES.OPERATOR) === evaluateeRole
    );
    return ev ? new Date(ev.date) : null;
  }, [evaluations, selectedEvaluateeId, selectedSectorId, evaluateeRole]);

  const handleSectorChange = (e) => {
    const id = e.target.value;
    setSelectedSectorId(id);
    setSelectedEvaluateeId("");
    setScores({});
    setPercentageScores({});
    setKpiNotes({});
    setSubmitError(null);
    setSubmitted(false);
    setKpisError(null);
  };

  const handleRoleChange = (role) => {
    setEvaluateeRole(role);
    setSelectedEvaluateeId("");
    setScores({});
    setPercentageScores({});
    setKpiNotes({});
    setSubmitError(null);
    setSubmitted(false);
    setKpisError(null);
  };

  const handleSelectEvaluatee = (id) => {
    setSelectedEvaluateeId(id);
    setSubmitError(null);
    setSubmitted(false);
  };

  const handleScoreChange = (kpiId, value) => {
    const num = parseInt(value, 10);
    if (num >= 1 && num <= 5) setScores((prev) => ({ ...prev, [kpiId]: num }));
  };

  const handlePercentageScoreChange = (kpiId, value) => {
    const str = String(value).trim();
    if (str === "") {
      setPercentageScores((prev) => {
        const next = { ...prev };
        delete next[kpiId];
        return next;
      });
      return;
    }
    const num = parseFloat(str);
    if (!Number.isNaN(num)) {
      const clamped = Math.min(100, Math.max(0, num));
      setPercentageScores((prev) => ({ ...prev, [kpiId]: clamped }));
    }
  };

  const handleKpiNoteChange = (kpiId, text) => {
    setKpiNotes((prev) => ({ ...prev, [kpiId]: text }));
  };

  const parseNumber = (raw) => {
    if (raw == null) return undefined;
    const s = String(raw).trim();
    if (s === "") return undefined;
    const cleaned = s.replace(/[^0-9.\-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleActualChange = (kpiId, value) => {
    const str = String(value).trim();
    if (str === "") {
      setActuals((prev) => {
        const next = { ...prev };
        delete next[kpiId];
        return next;
      });
      // remove auto percentage when actual cleared
      setPercentageScores((prev) => {
        const next = { ...prev };
        delete next[kpiId];
        return next;
      });
      return;
    }

    setActuals((prev) => ({ ...prev, [kpiId]: str }));

    // Auto-calc percentage = (actual / target) * 100 when both numeric
    const actualNum = parseNumber(str);
    const kpi = sectorKpis.find((k) => k.id === kpiId);
    const targetNum = parseNumber(kpi?.target);
    if (actualNum !== undefined && targetNum !== undefined && targetNum !== 0) {
      let perc = (actualNum / targetNum) * 100;
      if (!Number.isFinite(perc)) return;
      // clamp 0-100 and round to 1 decimal
      perc = Math.min(100, Math.max(0, perc));
      perc = Math.round(perc * 10) / 10;
      setPercentageScores((prev) => ({ ...prev, [kpiId]: perc }));
    }
  };

  const allScoresFilled = sectorKpis.every((k) => scores[k.id] >= 1 && scores[k.id] <= 5);

  const handleSaveDraft = () => {
    if (!selectedEvaluateeId) return;
    setDraft({
      evaluateeId: selectedEvaluateeId,
      sectorId: selectedSectorId,
      evaluateeRole,
      scores: { ...scores },
      percentageScores: { ...percentageScores },
      kpiNotes: { ...kpiNotes },
      actuals: { ...actuals },
      evaluatedAt: evaluatedAtLocal,
      savedAt: new Date().toISOString(),
    });
  };

  const formatEvaluatedAt = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${day} ${h}:${min}:${s}`;
  };

  // Helper to reset evaluatedAtLocal to now in datetime-local format
  const resetEvaluatedAtToNow = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setEvaluatedAtLocal(`${yyyy}-${mm}-${dd}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSectorId || !selectedEvaluateeId || !allScoresFilled) return;
    setSubmitError(null);
    setIsSubmitting(true);
    const evaluationId = `ev-${Date.now()}`;
    const evaluatedAt = formatEvaluatedAt(new Date());
    const roleParam = evaluateeRole === ROLES.OPERATOR ? "operator" : "supervisor";
    try {
      const postPromises = sectorKpis.map((kpi) => {
        // build a local Date at midnight for the selected YYYY-MM-DD
        const parts = (evaluatedAtLocal || "").split("-").map((x) => parseInt(x, 10));
        let localDate = new Date();
        if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
          localDate = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0);
        }
        return createEvaluationScore({
          evaluation_id: evaluationId,
          evaluatee_id: selectedEvaluateeId,
          evaluatee_name: selectedEvaluatee?.name || "",
          sector_id: selectedSectorId,
          sector_name: selectedSector?.name || "",
          evaluatee_role: roleParam,
          kpi_id: kpi.id,
          kpi_name: kpi.name || "",
          score_range: scores[kpi.id],
          score_percentage: percentageScores[kpi.id] != null ? percentageScores[kpi.id] : null,
          actual_value: actuals[kpi.id] != null ? actuals[kpi.id] : null,
          evaluated_at: formatEvaluatedAt(localDate),
        });
      });
      await Promise.all(postPromises);
      const payload = {
        id: evaluationId,
        sectorId: selectedSectorId,
        sectorName: selectedSector?.name,
        operatorId: selectedEvaluateeId,
        operatorName: selectedEvaluatee?.name,
        evaluateeId: selectedEvaluateeId,
        evaluateeName: selectedEvaluatee?.name,
        evaluateeRole,
        supervisorId: CURRENT_SUPERVISOR_ID,
        kpiScores: { ...scores },
        kpiPercentageScores: { ...percentageScores },
        kpiNotes: { ...kpiNotes },
        kpiActuals: { ...actuals },
        date: ((): string => {
          const parts = (evaluatedAtLocal || "").split("-").map((x) => parseInt(x, 10));
          if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
            return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0).toISOString();
          }
          return new Date().toISOString();
        })(),
      };
      addEvaluation(payload);
      setScores({});
      setPercentageScores({});
      setKpiNotes({});
      resetEvaluatedAtToNow();
      setSubmitted(true);
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to save evaluation";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEvaluate = selectedSectorId && selectedEvaluateeId && personnelInSector.some((p) => p.id === selectedEvaluateeId);

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6 max-w-[1440px] mx-auto">
      {/* Page header – match Dashboard style */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
              style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
            >
              Supervisor
            </span>
          </div>
          <h2 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Evaluation
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-2xl">
            Select a sector, choose Operator or Supervisor, then rate performance against role-specific KPIs.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left sidebar */}
        <aside className="w-full lg:w-80 flex flex-col gap-6 shrink-0">
          {/* Sector */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <span className="p-2 rounded-xl bg-[#0e76a8]/10 text-[#0e76a8]">
                <MdDomain className="text-lg" />
              </span>
              <h3 className="font-black text-slate-900 dark:text-white text-base">Sector</h3>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              {sectorsLoading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading sectors…</p>
              ) : sectors.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">No sectors yet. Add them in Sector Management.</p>
              ) : (
                <>
                  <div className="relative">
                    <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base pointer-events-none" />
                    <input
                      type="text"
                      value={sectorSearch}
                      onChange={(e) => setSectorSearch(e.target.value)}
                      placeholder="Search sectors…"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none"
                    />
                  </div>
                  <select
                    value={selectedSectorId}
                    onChange={handleSectorChange}
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none font-medium transition-shadow"
                  >
                    <option value="">Select sector…</option>
                    {filteredSectors.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {filteredSectors.length === 0 && sectorSearch.trim() && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">No sectors match your search.</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Role tabs */}
          {selectedSectorId && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden p-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Evaluate by role</h3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleRoleChange(ROLES.OPERATOR)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    evaluateeRole === ROLES.OPERATOR ? "text-white" : "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                  }`}
                  style={evaluateeRole === ROLES.OPERATOR ? { backgroundColor: SECONDARY } : {}}
                >
                  <MdPerson className="text-lg" />
                  Operator
                </button>
                <button
                  type="button"
                  onClick={() => handleRoleChange(ROLES.SUPERVISOR)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    evaluateeRole === ROLES.SUPERVISOR ? "text-white" : "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                  }`}
                  style={evaluateeRole === ROLES.SUPERVISOR ? { backgroundColor: PRIMARY } : {}}
                >
                  <MdSupervisorAccount className="text-lg" />
                  Supervisor
                </button>
              </div>
            </div>
          )}

          {/* Assigned personnel (Operators or Supervisors) */}
          {selectedSectorId && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden flex flex-col max-h-[380px]">
              <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="p-2 rounded-xl bg-[#f27405]/10 text-[#f27405]">
                    {evaluateeRole === ROLES.OPERATOR ? <MdPerson className="text-lg" /> : <MdSupervisorAccount className="text-lg" />}
                  </span>
                  <h3 className="font-black text-slate-900 dark:text-white text-base">
                    {evaluateeRole === ROLES.OPERATOR ? "Assigned Operators" : "Assigned Supervisors"}
                  </h3>
                </div>
                <MdSearch className="text-slate-400 text-xl shrink-0" />
              </div>
              <div className="p-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <input
                  type="text"
                  value={personnelSearch}
                  onChange={(e) => setPersonnelSearch(e.target.value)}
                  placeholder={evaluateeRole === ROLES.OPERATOR ? "Search operators…" : "Search supervisors…"}
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white text-sm px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400 transition-shadow"
                />
              </div>
              <div className="flex flex-col overflow-y-auto min-h-0">
                {personnelLoading ? (
                  <div className="p-6 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
                  </div>
                ) : personnelInSector.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {evaluateeRole === ROLES.OPERATOR ? "No operators in this sector." : "No supervisors in this sector."}
                    </p>
                  </div>
                ) : (
                  filteredPersonnel.map((p) => {
                    const isSelected = p.id === selectedEvaluateeId;
                    const isEvaluated = evaluations.some(
                      (e) => (e.evaluateeId || e.operatorId) === p.id && e.sectorId === selectedSectorId && (e.evaluateeRole || ROLES.OPERATOR) === evaluateeRole
                    );
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleSelectEvaluatee(p.id)}
                        className={`flex items-center gap-3 p-4 text-left w-full transition-all border-l-4 ${
                          isSelected
                            ? "bg-[#0e76a8]/10 dark:bg-[#0e76a8]/20 border-[#0e76a8]"
                            : "border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        }`}
                      >
                        <div
                          className={`size-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                            isSelected
                              ? "bg-[#0e76a8]/20 text-[#0e76a8] dark:bg-[#0e76a8]/30 dark:text-[#0e76a8]"
                              : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                          }`}
                        >
                          {(p.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{p.name || "—"}</p>
                          <p className={`text-xs truncate ${isSelected ? "font-semibold text-[#0e76a8]" : "text-slate-500 dark:text-slate-400"}`}>
                            {isEvaluated ? "Evaluated" : "Pending"}
                          </p>
                        </div>
                        {isSelected && <MdEdit className="text-lg shrink-0 text-[#0e76a8]" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col gap-6 min-w-0">
          {!selectedSectorId ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 md:p-12 text-center">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
                <MdDomain className="text-4xl text-slate-400" />
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium">Select a sector in the sidebar to start evaluating.</p>
            </div>
          ) : !canEvaluate ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 md:p-12 text-center">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
                {evaluateeRole === ROLES.OPERATOR ? <MdPerson className="text-4xl text-slate-400" /> : <MdSupervisorAccount className="text-4xl text-slate-400" />}
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium">
                Select an {evaluateeRole === ROLES.OPERATOR ? "operator" : "supervisor"} from the list to evaluate their performance.
              </p>
            </div>
          ) : (
            <>
              {/* Evaluation header */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-6">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-[#0e76a8] font-bold text-sm uppercase tracking-widest">
                      <MdAssignment className="text-lg" />
                      Performance Entry
                    </div>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                      Evaluate: {selectedEvaluatee?.name}
                    </h1>
                    {/* <p className="text-slate-500 dark:text-slate-400 text-sm">
                      ID · {selectedSector?.name ?? "—"} · {getShiftLabel().split(" - ")[0]}
                    </p> */}
                  </div>
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full shrink-0">
                    <MdSchedule className="text-sm" />
                    {lastEntryForEvaluatee
                      ? `Last entry: ${lastEntryForEvaluatee.toLocaleDateString() === new Date().toLocaleDateString() ? "Today" : `${Math.max(1, Math.floor((Date.now() - lastEntryForEvaluatee) / 86400000))} days ago`}`
                      : "No previous entry"}
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="evaluated-at" className="text-xs text-slate-500 dark:text-slate-400">Evaluated on</label>
                    <input
                      id="evaluated-at"
                      type="date"
                      value={evaluatedAtLocal}
                      onChange={(e) => setEvaluatedAtLocal(e.target.value)}
                      className="ml-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-1.5"
                    />
                  </div>
                </div>
              </div>

              {/* KPI Scoring */}
              {kpisLoading ? (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 md:p-12 text-center">
                  <p className="text-sm text-slate-500">Loading KPIs…</p>
                </div>
              ) : kpisError ? (
                <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-8 md:p-12 text-center">
                  <p className="text-sm text-red-500">{kpisError}</p>
                  <button
                    type="button"
                    onClick={() => fetchKpis(selectedSectorId, evaluateeRole)}
                    className="mt-3 flex items-center gap-2 text-sm font-bold text-[#0e76a8] hover:underline mx-auto"
                  >
                    <MdRefresh className="text-lg" />
                    Retry
                  </button>
                </div>
              ) : sectorKpis.length === 0 ? (
                <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6 text-center">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    No {evaluateeRole === ROLES.OPERATOR ? "operator" : "supervisor"} KPIs defined for this sector. Add them in Sector Management & KPI.
                  </p>
                </div>
              ) : (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-wrap justify-between items-center gap-3">
                  <h3 className="font-black text-slate-700 dark:text-slate-300 text-sm uppercase tracking-wider">
                    KPI Performance Scoring
                  </h3>
                  <div className="flex gap-3 text-xs font-bold text-slate-500 dark:text-slate-400">
                    <span>1 = Poor</span>
                    <span>5 = Excellent</span>
                  </div>
                </div>
                {submitError && (
                  <div className="mx-4 mt-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">{submitError}</p>
                  </div>
                )}
                <form id="eval-form" onSubmit={handleSubmit} className="divide-y divide-slate-100 dark:divide-slate-800">
                  {sectorKpis.map((kpi, idx) => {
                    const Icon = KPI_ICONS[idx] || MdBarChart;
                    const iconColor = kpi.iconColor || "text-slate-500";
                    return (
                      <div key={kpi.id} className="p-4 sm:p-6 flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                        <div className="w-full md:w-1/3 shrink-0">
                          <div className={`flex items-center gap-2 mb-1.5 ${iconColor}`}>
                            <Icon className="text-xl shrink-0" />
                            <h4 className="font-bold text-slate-900 dark:text-white">{kpi.name}</h4>
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{kpi.definition}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                            <span className="font-semibold text-slate-700 dark:text-white">Target:</span>{' '}
                            <span className="inline-block bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-xl text-xs font-medium">{kpi.target || "—"}</span>
                          </p>
                        </div>
                        <div className="flex-1 w-full flex flex-col gap-3">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full max-w-xs gap-0.5">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <React.Fragment key={n}>
                                  <input
                                    type="radio"
                                    name={`score-${kpi.id}`}
                                    id={`${kpi.id}-${n}`}
                                    checked={scores[kpi.id] === n}
                                    onChange={() => handleScoreChange(kpi.id, n)}
                                    className="sr-only"
                                  />
                                  <label
                                    htmlFor={`${kpi.id}-${n}`}
                                    className={`flex-1 text-center py-2.5 text-sm font-bold rounded-lg cursor-pointer transition-all ${
                                      scores[kpi.id] === n
                                        ? "text-white shadow-sm"
                                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                    }`}
                                    style={scores[kpi.id] === n ? { backgroundColor: SECONDARY } : {}}
                                  >
                                    {n}
                                  </label>
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="flex items-center gap-2 min-w-0">
                              <label htmlFor={`percentage-${kpi.id}`} className="text-sm font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                Score (%)
                              </label>
                              <input
                                id={`percentage-${kpi.id}`}
                                type="number"
                                min={0}
                                max={100}
                                step={0.5}
                                value={percentageScores[kpi.id] ?? ""}
                                readOnly
                                placeholder="0–100"
                                className="w-24 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none cursor-not-allowed"
                              />
                            <div className="flex items-center gap-2 min-w-0">
                              <label htmlFor={`actual-${kpi.id}`} className="text-sm font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                Actual
                              </label>
                              <input
                                id={`actual-${kpi.id}`}
                                type="text"
                                value={actuals[kpi.id] ?? ""}
                                onChange={(e) => handleActualChange(kpi.id, e.target.value)}
                                placeholder="Actual"
                                className="w-28 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400"
                              />
                            </div>
                            </div>
                          </div>
                          <textarea
                            value={kpiNotes[kpi.id] ?? ""}
                            onChange={(e) => handleKpiNoteChange(kpi.id, e.target.value)}
                            placeholder={`Notes for ${kpi.name}…`}
                            rows={2}
                            className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:border-[#0e76a8] focus:outline-none placeholder-slate-400 resize-none min-h-[56px] transition-shadow"
                          />
                        </div>
                      </div>
                    );
                  })}
                </form>
                <div className="p-4 sm:p-5 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    Save Draft
                  </button>
                  <button
                    type="submit"
                    form="eval-form"
                    disabled={!allScoresFilled || isSubmitting}
                    className="px-6 py-2.5 rounded-xl text-white font-black text-sm shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: SECONDARY, boxShadow: `${SECONDARY}33 0 10px 20px` }}
                  >
                    <MdSend className={`text-lg ${isSubmitting ? "animate-pulse" : ""}`} />
                    {isSubmitting ? "Saving…" : "Submit Performance Data"}
                  </button>
                </div>
              </div>
              )}

              {submitted && (
                <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex items-center gap-3">
                  <span className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
                    <MdCheckCircle className="text-xl text-emerald-600 dark:text-emerald-400" />
                  </span>
                  <span className="font-bold text-emerald-800 dark:text-emerald-200">Evaluation submitted successfully!</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
